// Координатная шкала карты: линейки по четырём краям и серая сетка.
//
// Шаг сетки НЕ фиксирован в градусах: на обзоре 0.25° дали бы кашу из сотен
// линий, на подходе к точке 5° — ни одной. Поэтому шаг подбирается под ширину
// видимой области так, чтобы делений было немного и все — на круглых значениях:
// сетка должна стоять на 45°, 46°, 47°, а не плыть вместе с краем экрана.

/** Сколько делений сетка даёт по умолчанию («обычная» густота).
 *
 *  Это ПОТОЛОК, а не абсолютный шаг: настройка густоты (graticuleDensity.ts)
 *  двигает именно его. Капитаны просили сетку мельче — крупнее потолок, мельче
 *  круглый шаг, и так на любом масштабе: густота переживает зум, потому что она
 *  множитель, а не величина в градусах. */
export const GRATICULE_MAX_LINES = 8

/** Ближе этого подписи соседних делений наезжают друг на друга (10px-шрифт,
 *  самая длинная подпись — «-179°30′»). Мерено по вёрстке MapGraticule. */
export const LABEL_MIN_GAP_PX = 56

/** Круглые шаги ПО ВОЗРАСТАНИЮ: секунды, минуты, градусы.
 *
 *  Порядок важен: ищем САМЫЙ МЕЛКИЙ шаг, при котором линий не больше потолка.
 *  На убывающем списке поиск «первый ≥ нужного» всегда возвращал бы 30°. */
const NICE_STEPS = [
  1 / 360, // 10″
  1 / 120, // 30″
  1 / 60, // 1′
  1 / 30, // 2′
  1 / 12, // 5′
  1 / 6, // 10′
  1 / 3, // 20′
  0.5, // 30′
  1,
  2,
  5,
  10,
  20,
  30,
]

/** Шаг сетки под ширину видимой области (в градусах) при заданном потолке линий. */
export function graticuleStep(spanDeg: number, maxLines: number = GRATICULE_MAX_LINES): number {
  const span = Math.abs(spanDeg)
  // Вырожденный масштаб (карта ещё не измерена) — самый мелкий шаг.
  if (!(span > 0)) return NICE_STEPS[0]
  const cap = maxLines > 0 ? maxLines : GRATICULE_MAX_LINES
  const wanted = span / cap
  // Самый мелкий круглый шаг, при котором линий не больше потолка.
  return NICE_STEPS.find(s => s >= wanted) ?? NICE_STEPS[NICE_STEPS.length - 1]
}

/** Через сколько делений ставить подпись, чтобы подписи не наезжали.
 *
 *  Считается по РАССТОЯНИЮ между линиями на экране, а не по их числу: на густой
 *  сетке линии нужны все (по ним и снимают координату), а подписи — только те,
 *  что читаются. Вырожденный вход (карта ещё не измерена) → подписываем каждую. */
export function labelStride(spacingPx: number, minGapPx: number = LABEL_MIN_GAP_PX): number {
  if (!Number.isFinite(spacingPx) || spacingPx <= 0) return 1
  return Math.max(1, Math.ceil(minGapPx / spacingPx))
}

/** Шаг ПОДПИСЕЙ: не мельче `step · stride` и по-прежнему круглый.
 *
 *  Просто умножить шаг на прореживание нельзя: 20′ × 4 = 1°20′ — подпись на
 *  некруглом значении, ровно то, ради чего заведён NICE_STEPS. Ищем ближайший
 *  круглый шаг, который делится на шаг сетки нацело, — тогда подписанные линии
 *  и правда существуют среди нарисованных. */
function labelStep(step: number, stride: number): number {
  if (stride <= 1) return step
  for (let k = stride; k <= stride * 8; k += 1) {
    const cand = step * k
    if (NICE_STEPS.some(n => Math.abs(n - cand) < 1e-9)) return cand
  }
  return step * stride
}

/** Отметки, кратные шагу, внутри [min, max]. */
export function ticks(min: number, max: number, step: number): number[] {
  if (!(step > 0) || !Number.isFinite(min) || !Number.isFinite(max)) return []
  const lo = Math.min(min, max)
  const hi = Math.max(min, max)
  const out: number[] = []
  const first = Math.ceil(lo / step - 1e-9)
  const last = Math.floor(hi / step + 1e-9)
  for (let i = first; i <= last; i += 1) {
    // Умножение, а не накопление суммой: иначе 1/60 копит ошибку по экрану.
    out.push(round(i * step))
  }
  return out
}

/** Подпись деления. Мельче градуса — в градусах и минутах.
 *
 *  Долгота приводится к ±180: MapLibre отдаёт границы «развёрнутыми» за
 *  антимеридиан (175…185), и без нормализации у Курил появилась бы подпись
 *  «185°», которой нет ни на одной карте. */
export function formatTick(value: number, step: number): string {
  const v = normalizeLon(value)
  if (step >= 1) return `${Math.round(v)}°`

  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  const deg = Math.floor(abs)
  // Мельче минуты — добавляем секунды, иначе соседние деления получили бы
  // ОДИНАКОВУЮ подпись, и шкала начала бы врать.
  const withSeconds = step < 1 / 60
  const totalSec = Math.round((abs - deg) * 3600)
  let d = deg
  let m = withSeconds ? Math.floor(totalSec / 60) : Math.round((abs - deg) * 60)
  const sec = totalSec % 60
  if (m === 60) {
    d += 1
    m = 0
  }
  const mm = String(m).padStart(2, '0')
  return withSeconds
    ? `${sign}${d}°${mm}′${String(sec).padStart(2, '0')}″`
    : `${sign}${d}°${mm}′`
}

/** −180 < x ≤ 180. Ровно 180 оставляем положительным: на картах это «180°». */
function normalizeLon(value: number): number {
  if (value >= -180 && value <= 180) return value
  const wrapped = ((value + 180) % 360 + 360) % 360 - 180
  return wrapped === -180 ? 180 : wrapped
}

function round(n: number): number {
  return Math.abs(n) < 1e-9 ? 0 : Number(n.toFixed(6))
}

/** Отметка шкалы: позиция на экране в CSS-пикселях (x для меридианов, y для
 *  параллелей) и подпись. */
export interface GraticuleTick {
  px: number
  label: string
  /** Ставить ли подпись. На густой сетке подписаны не все линии — иначе подписи
   *  наезжают друг на друга; линия при этом рисуется всё равно. */
  labelled: boolean
}

/** Снимок шкалы для одного кадра.
 *
 *  `width`/`height` — размер полотна карты в тех же CSS-пикселях, в которых
 *  посчитаны `px`. Без них снимок не переложить на скачиваемый кадр: полотно
 *  карты живёт в device-пикселях (retina — вдвое крупнее CSS), а видео ещё и
 *  ужимается под потолок кодировщика. */
export interface GraticuleFrame {
  lon: GraticuleTick[]
  lat: GraticuleTick[]
  width: number
  height: number
}

/** То немногое, что шкале нужно от карты. Узкий интерфейс — ради тестов:
 *  настоящий MapLibre в них не поднять, а геометрия здесь простая. */
export interface GraticuleMap {
  getBounds(): { getWest(): number; getEast(): number; getSouth(): number; getNorth(): number }
  project(lngLat: [number, number]): { x: number; y: number }
  getCanvas(): { clientWidth: number; clientHeight: number }
}

/** Деления шкалы для текущего вида карты.
 *
 *  Один расчёт на два выхода: шкалу на экране (MapGraticule) и сетку на
 *  скачиваемом кадре (exportImage). Разойдись они — картинка перестала бы
 *  совпадать с тем, что человек видел, когда жал кнопку. */
export function graticuleFrame(
  map: GraticuleMap,
  maxLines: number = GRATICULE_MAX_LINES,
): GraticuleFrame {
  const b = map.getBounds()
  const west = b.getWest()
  const east = b.getEast()
  const south = b.getSouth()
  const north = b.getNorth()

  const lonStep = graticuleStep(east - west, maxLines)
  const latStep = graticuleStep(north - south, maxLines)
  const midLat = (north + south) / 2
  const midLon = (east + west) / 2
  const canvas = map.getCanvas()

  // Без поворота x зависит только от долготы, y — только от широты, поэтому
  // вторую координату берём из середины экрана.
  const lonPx = (v: number) => map.project([v, midLat]).x
  const latPx = (v: number) => map.project([midLon, v]).y

  return {
    lon: axis(ticks(west, east, lonStep), lonStep, lonPx),
    lat: axis(ticks(south, north, latStep), latStep, latPx),
    width: canvas.clientWidth,
    height: canvas.clientHeight,
  }
}

/** Значения одной оси → отметки с позициями и решением «подписывать ли».
 *
 *  Подписи выбираются ПО ЗНАЧЕНИЮ (кратно шагу подписей), а не по номеру в
 *  списке: номер зависит от края экрана, и при панорамировании подпись
 *  перебегала бы с 163° на 163°20′ и обратно — та же болезнь, от которой
 *  отметки стоят на круглых значениях. */
function axis(values: number[], step: number, px: (v: number) => number): GraticuleTick[] {
  const marks = values.map(v => ({ v, px: px(v) }))
  const spacing = marks.length > 1 ? Math.abs(marks[1].px - marks[0].px) : Infinity
  const lStep = labelStep(step, labelStride(spacing))
  return marks.map(m => ({
    px: m.px,
    // Подпись форматируется шагом ПОДПИСЕЙ, а не шагом линий: при делениях по
    // 30′ подписаны целые градусы, и «45°00′» показывало бы минуты, которыми
    // соседние подписи всё равно не различаются.
    label: formatTick(m.v, lStep),
    labelled: Math.abs(m.v / lStep - Math.round(m.v / lStep)) < 1e-6,
  }))
}
